import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ShoppingCart, Star, TrendingUp } from "lucide-react";
import { useProducts } from "@/hooks/useProducts";
import { useCart } from "@/contexts/CartContext";
import { Skeleton } from "@/components/ui/skeleton";

const EmptyCartRecommendations = () => {
  const { data: products, isLoading } = useProducts();
  const { addToCart } = useCart();

  // Get top 3 products as recommendations
  const recommendedProducts = products?.slice(0, 3) || [];

  const handleQuickAdd = (product: any) => {
    const packSize = product.packSizes?.[0]?.size || 12;
    addToCart(product, packSize, false);
  };

  if (isLoading) {
    return (
      <div className="mt-8">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-solr-nebula-purple" />
          Popular Products
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="cosmic-card p-4">
              <Skeleton className="w-full h-32 rounded-lg mb-3" />
              <Skeleton className="h-4 w-3/4 mb-2" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (recommendedProducts.length === 0) return null;

  return (
    <div className="mt-8">
      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <TrendingUp className="w-5 h-5 text-solr-nebula-purple" />
        Popular Products You Might Like
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {recommendedProducts.map((product) => {
          const price = product.packSizes?.[0]?.price || product.price;
          const subscriptionPrice = product.packSizes?.[0]?.subscription_price || price * 0.85;
          
          return (
            <div key={product.id} className="cosmic-card p-4 group hover:cosmic-shadow transition-all duration-300">
              <Link to={`/shop/${product.id}`} className="block">
                <div className="aspect-square rounded-lg overflow-hidden mb-3 bg-solr-nebula-purple/5 flex items-center justify-center">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="object-contain w-full h-full max-h-full group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-3 h-3 fill-solr-synapse-gold text-solr-synapse-gold" />
                    ))}
                    <span className="text-xs text-solr-starfield-silver ml-1">4.9</span>
                  </div>
                  <h4 className="font-medium text-sm line-clamp-1">{product.name}</h4>
                  <p className="text-xs text-solr-starfield-silver line-clamp-1">{product.tagline}</p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold">£{subscriptionPrice.toFixed(2)}</span>
                    <span className="text-xs text-solr-starfield-silver line-through">£{price.toFixed(2)}</span>
                    <span className="text-xs text-solr-synapse-gold">Save 15%</span>
                  </div>
                </div>
              </Link>
              <Button 
                onClick={(e) => {
                  e.preventDefault();
                  handleQuickAdd(product);
                }}
                className="btn-primary w-full mt-3 text-xs py-2"
                size="sm"
              >
                <ShoppingCart className="w-3.5 h-3.5 mr-1.5" />
                Add to Cart
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EmptyCartRecommendations;
